import { createStart, createCsrfMiddleware, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { resolveRedirect } from "./lib/redirect-map";

/**
 * Real server-side 301s for retired URLs, plus a genuine 404 status (with
 * robots: noindex) for unknown paths. Runs before routing, so no legacy URL
 * can be served as a 200 HTML shell.
 */
const seoResponseMiddleware = createMiddleware({ type: "request" }).server(
  async ({ request, pathname, next, handlerType }) => {
    if (handlerType === "router") {
      const destination = resolveRedirect(pathname);
      if (destination) {
        const { search } = new URL(request.url);
        return new Response(null, {
          status: 301,
          headers: {
            location: destination + search,
            "cache-control": "public, max-age=3600",
          },
        });
      }
    }

    const result = await next();

    // Unknown routes already resolve to a 404 status; make sure the HTML they
    // return is not advertised to crawlers as indexable.
    const response = result.response;
    if (
      handlerType === "router" &&
      response.status === 404 &&
      (response.headers.get("content-type") ?? "").includes("text/html")
    ) {
      const html = await response.text();
      const inheritedCanonical = /<link[^>]*rel="canonical"[^>]*>/i.test(html);
      const notFoundHtml = html
        .replace(
          /<meta name="robots" content="[^"]*"/g,
          '<meta name="robots" content="noindex, nofollow"',
        )
        .replace(/<link[^>]*rel="canonical"[^>]*>/gi, "")
        .replace(/<meta[^>]*(?:property|name)="og:url"[^>]*>/gi, "")
        .replace(
          inheritedCanonical ? /<title>[^<]*<\/title>/i : /$^/,
          "<title>Page Not Found | Hadley Energy Solutions</title>",
        );
      return new Response(notFoundHtml, {
        status: 404,
        headers: response.headers,
      });
    }

    return result;
  },
);

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Start installs this automatically when src/start.ts is absent; defining the
// file opts out, so re-add it explicitly to keep server functions protected
// from cross-site requests.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  requestMiddleware: [seoResponseMiddleware, errorMiddleware, csrfMiddleware],
}));
